import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase/client';
import { TypingIndicator } from '../types';

const TYPING_TIMEOUT = 3000; // 3 seconds

/**
 * Hook to manage typing indicators in a chat
 */
export const useTypingIndicator = (chatId: string, userId: string) => {
  const [typingUsers, setTypingUsers] = useState<TypingIndicator[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!chatId) return;

    // Subscribe to typing indicators
    const channel = supabase
      .channel(`typing:${chatId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'typing_indicators',
          filter: `chat_id=eq.${chatId}`,
        },
        async () => {
          // Fetch current typing users
          const { data } = await supabase
            .from('typing_indicators')
            .select(`
              *,
              user:profiles(*)
            `)
            .eq('chat_id', chatId)
            .neq('user_id', userId)
            .gte('updated_at', new Date(Date.now() - TYPING_TIMEOUT).toISOString());

          setTypingUsers(data || []);
        }
      )
      .subscribe();

    // Cleanup old typing indicators periodically
    const interval = setInterval(async () => {
      try {
        await supabase
          .from('typing_indicators')
          .delete()
          .eq('chat_id', chatId)
          .lt('updated_at', new Date(Date.now() - TYPING_TIMEOUT).toISOString());
      } catch (err) {
        console.error('Error cleaning typing indicators:', err);
      }
    }, 5000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [chatId, userId]);

  /**
   * Set current user as typing
   */
  const setTyping = async () => {
    try {
      await supabase
        .from('typing_indicators')
        .upsert({
          chat_id: chatId,
          user_id: userId,
          updated_at: new Date().toISOString(),
        });

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Auto-clear after timeout
      timeoutRef.current = setTimeout(() => {
        stopTyping();
      }, TYPING_TIMEOUT);
    } catch (error) {
      console.error('Error setting typing:', error);
    }
  };

  /**
   * Stop typing indicator
   */
  const stopTyping = async () => {
    try {
      await supabase
        .from('typing_indicators')
        .delete()
        .eq('chat_id', chatId)
        .eq('user_id', userId);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    } catch (error) {
      console.error('Error stopping typing:', error);
    }
  };

  /**
   * Get typing indicator text
   */
  const getTypingText = (): string | null => {
    if (typingUsers.length === 0) return null;
    if (typingUsers.length === 1) {
      const name = typingUsers[0].user?.nickname || typingUsers[0].user?.full_name || 'Someone';
      return `${name} is typing...`;
    }
    if (typingUsers.length === 2) {
      return `${typingUsers[0].user?.nickname || 'Someone'} and ${typingUsers[1].user?.nickname || 'someone'} are typing...`;
    }
    return 'Several people are typing...';
  };

  return {
    typingUsers,
    typingText: getTypingText(),
    setTyping,
    stopTyping,
  };
};

